import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, LogOut, ChevronUp, BookOpen, Shield, Calculator, Heart, Menu, Sun, Moon, Monitor, Compass, Package, MessageSquarePlus, ShoppingCart, CalendarHeart, Bot } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import { useRealtimeSyncContext } from "@/contexts/RealtimeSyncContext";
import { useShoppingList } from "@/hooks/useShoppingList";
import { ShoppingListDrawer } from "@/components/ShoppingListDrawer";
import { LanguageSelector } from "@/components/LanguageSelector";
import { Logo } from "@/components/Logo";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import { useAdminCheck } from "@/hooks/useAdminCheck";
import { useUserProfile } from "@/hooks/useUserProfile";
import { toast } from "sonner";
import { FeedbackForm } from "@/components/FeedbackForm";
import { toastLoginRequired } from "@/utils/toastLoginRequired";

const routePrefetchers: Record<string, () => Promise<unknown>> = {
  "/": () => import("@/pages/Index"),
  "/meu-armario": () => import("@/pages/MeuArmario"),
  "/calendario-emocional": () => import("@/pages/CalendarioEmocional"),
  "/assistente": () => import("@/pages/Assistente"),
  "/favoritos": () => import("@/pages/Favoritos"),
  "/blog": () => import("@/pages/Blog"),
  "/perfil": () => import("@/pages/Profile"),
  "/admin": () => import("@/pages/Admin"),
  "/?calculator=open": () => import("@/pages/Index"),
};

function prefetchRoute(path: string) {
  routePrefetchers[path]?.().catch(() => {});
}

export function AppSidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpenMobile, toggleSidebar } = useSidebar();
  const { user, isAuthenticated, signOut } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { name: profileName, avatarUrl } = useUserProfile();
  const [isThemeOpen, setIsThemeOpen] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const { favorites, cabinetOils, isLoading } = useRealtimeSyncContext();
  const { pendingItems } = useShoppingList();

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      toast.error("Erro ao sair");
    } else {
      toast.success("Você saiu da conta");
      navigate("/");
    }
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const handleNavigate = (path: string) => {
    setOpenMobile(false);
    navigate(path);
  };

  const handleFeedbackClick = () => {
    if (!isAuthenticated) {
      toastLoginRequired(t("auth.loginToFeedback"));
      return;
    }
    setShowFeedbackForm(true);
  };

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/" && !location.search.includes("calculator");
    }
    if (path === "/?calculator=open") {
      return location.search.includes("calculator");
    }
    return location.pathname === path;
  };

  const activeClass = "bg-primary/10 text-primary font-medium border-l-4 border-primary -ml-1 pl-4";
  const inactiveClass = "";

  return (
    <Sidebar className="border-r">
      <SidebarHeader className="border-b px-4 py-4">
        <div className="flex items-center justify-between gap-3">
          <Logo variant="full" size="md" asLink />
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={toggleSidebar}
            className="h-10 w-10 flex-shrink-0 animate-slide-in-right hover:bg-accent"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">{t("nav.closeMenu")}</span>
          </Button>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-4 py-6">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => handleNavigate("/")}
              onMouseEnter={() => prefetchRoute("/")}
              onFocus={() => prefetchRoute("/")}
              className={`h-12 text-base transition-colors ${isActive("/") ? activeClass : inactiveClass}`}
            >
              <Compass className="h-5 w-5" />
              <span>{t("nav.explore")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => handleNavigate("/assistente")}
              onMouseEnter={() => prefetchRoute("/assistente")}
              onFocus={() => prefetchRoute("/assistente")}
              className={`h-12 text-base transition-colors ${isActive("/assistente") ? activeClass : inactiveClass}`}
            >
              <Bot className="h-5 w-5" />
              <span>{t("nav.assistant")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => handleNavigate("/meu-armario")}
              onMouseEnter={() => prefetchRoute("/meu-armario")}
              onFocus={() => prefetchRoute("/meu-armario")}
              className={`h-12 text-base transition-colors ${isActive("/meu-armario") ? activeClass : inactiveClass}`}
            >
              <Package className="h-5 w-5" />
              <span className="flex-1">{t("nav.cabinet")}</span>
              {!isLoading && cabinetOils.length > 0 && (
                <Badge variant="secondary" className="ml-auto bg-primary/10 text-primary">
                  {cabinetOils.length}
                </Badge>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => handleNavigate("/calendario-emocional")}
              onMouseEnter={() => prefetchRoute("/calendario-emocional")}
              onFocus={() => prefetchRoute("/calendario-emocional")}
              className={`h-12 text-base transition-colors ${isActive("/calendario-emocional") ? activeClass : inactiveClass}`}
            >
              <CalendarHeart className="h-5 w-5" />
              <span>{t("nav.emotionalCalendar")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton 
              size="lg"
              onClick={() => setShowShoppingList(true)}
              className="h-12 text-base transition-colors"
              data-shopping-trigger
            >
              <ShoppingCart className="h-5 w-5" />
              <span className="flex-1">{t("nav.shoppingList")}</span>
              {pendingItems.length > 0 && (
                <Badge variant="secondary" className="ml-auto bg-primary/10 text-primary">
                  {pendingItems.length}
                </Badge>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => handleNavigate("/blog")}
              onMouseEnter={() => prefetchRoute("/blog")}
              onFocus={() => prefetchRoute("/blog")}
              className={`h-12 text-base transition-colors ${isActive("/blog") ? activeClass : inactiveClass}`}
            >
              <BookOpen className="h-5 w-5" />
              <span>{t("nav.blog")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => handleNavigate("/?calculator=open")}
              onMouseEnter={() => prefetchRoute("/?calculator=open")}
              onFocus={() => prefetchRoute("/?calculator=open")}
              className={`h-12 text-base transition-colors ${isActive("/?calculator=open") ? activeClass : inactiveClass}`}
            >
              <Calculator className="h-5 w-5" />
              <span>{t("nav.calculator")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              onClick={() => handleNavigate("/favoritos")}
              onMouseEnter={() => prefetchRoute("/favoritos")}
              onFocus={() => prefetchRoute("/favoritos")}
              className={`h-12 text-base transition-colors ${isActive("/favoritos") ? activeClass : inactiveClass}`}
            >
              <Heart className="h-5 w-5" />
              <span className="flex-1">{t("nav.favorites")}</span>
              {!isLoading && favorites.length > 0 && (
                <Badge variant="secondary" className="ml-auto bg-primary/10 text-primary">
                  {favorites.length}
                </Badge>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <Collapsible open={isThemeOpen} onOpenChange={setIsThemeOpen}>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton 
                  size="lg"
                  className="h-12 text-base w-full"
                >
                  {resolvedTheme === "dark" ? (
                    <Moon className="h-5 w-5" />
                  ) : (
                    <Sun className="h-5 w-5" />
                  )}
                  <span className="flex-1">{t("nav.appearance")}</span>
                  <ChevronUp 
                    className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                      isThemeOpen ? "" : "rotate-180"
                    }`} 
                  />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-7 space-y-1 mt-1">
                <button
                  onClick={() => setTheme("light")}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-base transition-colors hover:bg-accent ${
                    theme === "light" ? "bg-primary/10 text-primary font-medium" : ""
                  }`}
                >
                  <Sun className="h-4 w-4" />
                  <span>{t("theme.light")}</span>
                  {theme === "light" && <span className="ml-auto text-primary">✓</span>}
                </button>
                <button
                  onClick={() => setTheme("dark")}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-base transition-colors hover:bg-accent ${
                    theme === "dark" ? "bg-primary/10 text-primary font-medium" : ""
                  }`}
                >
                  <Moon className="h-4 w-4" />
                  <span>{t("theme.dark")}</span>
                  {theme === "dark" && <span className="ml-auto text-primary">✓</span>}
                </button>
                <button
                  onClick={() => setTheme("system")}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-base transition-colors hover:bg-accent ${
                    theme === "system" ? "bg-primary/10 text-primary font-medium" : ""
                  }`}
                >
                  <Monitor className="h-4 w-4" />
                  <span>{t("theme.system")}</span>
                  {theme === "system" && <span className="ml-auto text-primary">✓</span>}
                </button>
              </CollapsibleContent>
            </Collapsible>
          </SidebarMenuItem>

          {/* Language Selector */}
          <SidebarMenuItem>
            <div className="px-1">
              <LanguageSelector />
            </div>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton 
              size="lg"
              onClick={handleFeedbackClick}
              className="h-12 text-base transition-colors"
            >
              <MessageSquarePlus className="h-5 w-5" />
              <span>{t("nav.feedback")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton
                size="lg"
                onClick={() => handleNavigate("/admin")}
                onMouseEnter={() => prefetchRoute("/admin")}
                onFocus={() => prefetchRoute("/admin")}
                className={`h-12 text-base transition-colors ${isActive("/admin") ? activeClass : inactiveClass}`}
              >
                <Shield className="h-5 w-5" />
                <span>{t("nav.admin")}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter className="border-t px-4 py-4">
        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center gap-3 rounded-lg p-2 hover:bg-accent transition-colors">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={avatarUrl || undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getInitials(profileName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">
                    {profileName || "Usuário"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
                <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              side="top" 
              align="start" 
              className="w-56 bg-popover border"
            >
              <DropdownMenuItem
                onClick={() => handleNavigate("/perfil")}
                onMouseEnter={() => prefetchRoute("/perfil")}
                className="cursor-pointer"
              >
                <User className="mr-2 h-4 w-4" />
                {t("nav.myProfile")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleSignOut} 
                className="text-destructive focus:text-destructive cursor-pointer"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("nav.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            variant="outline"
            className="w-full justify-start gap-3"
            onClick={() => handleNavigate("/auth")}
          >
            <User className="h-4 w-4" />
            {t("nav.signIn")}
          </Button>
        )}
      </SidebarFooter>

      <FeedbackForm open={showFeedbackForm} onOpenChange={setShowFeedbackForm} />
      <ShoppingListDrawer open={showShoppingList} onOpenChange={setShowShoppingList} />
    </Sidebar>
  );
}
